import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Loader2, Send, Paperclip, X, Wand2, FileText, Image as ImageIcon, ArrowLeft, Check } from "lucide-react";
import { DocAiPreview } from "@/components/DocAiPreview";
import { ChatMarkdown } from "@/components/ChatMarkdown";
import { AiWorking } from "@/components/AiWorking";
import { toast } from "sonner";

const CRIMSON = "var(--brand)";

// `template` (the editor's current form) switches the dialog to "ask about / change this existing form" mode.
export const AiDocBuilderDialog = ({ open, onOpenChange, onBuilt, template = null }) => {
  const isEdit = !!template;
  const [proposal, setProposal] = useState(null); // preview of the built/edited form
  const [sid, setSid] = useState(null);
  const [starting, setStarting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [models, setModels] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [building, setBuilding] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setMessages([]); setText(""); setFiles([]); setSid(null); setProposal(null);
    setStarting(true);
    Promise.all([api.docAiNewSession(template), api.docAiConfig()])
      .then(([s, cfg]) => { setSid(s.session_id); setSettings(cfg.settings); setModels(cfg.models || []); })
      .catch(() => toast.error("Couldn't start the AI builder. Try again."))
      .finally(() => setStarting(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, building]);

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((f) => [...f, ...picked]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const send = async () => {
    if (!sid || (!text.trim() && files.length === 0)) return;
    setSending(true);
    const fd = new FormData();
    fd.append("text", text);
    files.forEach((f) => fd.append("files", f));
    // optimistic user bubble
    setMessages((m) => [...m, { role: "user", text, files: files.map((f) => ({ name: f.name, kind: f.type.startsWith("image/") ? "image" : "document" })) }]);
    setText(""); setFiles([]);
    try {
      const res = await api.docAiMessage(sid, fd);
      setMessages(res.messages || []);
      if (res.skipped?.length) toast.warning(`Couldn't read: ${res.skipped.join(", ")}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "The assistant didn't respond. Try again.");
      setMessages((m) => m.slice(0, -1)); // roll back optimistic bubble on failure
    } finally { setSending(false); }
  };

  const build = async () => {
    if (!sid) return;
    setBuilding(true);
    try {
      const res = await api.docAiBuild(sid);
      setProposal(res); // show the preview; nothing is applied until the user says so
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't build the form. Try again or refine the plan.");
    } finally { setBuilding(false); }
  };

  // Preview is only available directly after the AI has proposed a plan; any further chat turns it off again.
  const last = messages[messages.length - 1];
  const planReady = !!last && last.role === "assistant" && !!last.plan;

  const apply = () => {
    onBuilt(proposal.template);
    toast.success(isEdit ? "Changes loaded into the editor — review, then Save." : "Form loaded into the editor — review and tweak, then Save.");
    onOpenChange(false);
  };
  const backToChat = () => {
    setProposal(null);
    // the server records the draft in the transcript; mirror it locally
    setMessages((m) => [...m, { role: "assistant", text: "✅ Drafted a preview of the changes — review it, or tell me what to adjust." }]);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!building && !sending) onOpenChange(v); }}>
      <DialogContent className={`${proposal ? "sm:max-w-5xl" : "sm:max-w-2xl"} p-0 gap-0 overflow-hidden`} data-testid="ai-doc-builder-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="text-base font-brand flex items-center gap-2">
            <Sparkles className="h-4 w-4" style={{ color: CRIMSON }} />{proposal ? (isEdit ? "Preview of proposed changes" : "Preview of the new form") : isEdit ? "Ask AI about this form" : "AI Document Builder"}
          </DialogTitle>
          {settings && (
            <div className="text-[11px] text-slate-400 mt-1" data-testid="ai-model-caption">
              Model: <span className="text-slate-500">{models.find((m) => m.id === settings.model)?.label?.split(" — ")[0] || settings.model}</span>
              {" · "}Thinking: <span className="text-slate-500 capitalize">{settings.thinking}</span>
              <span className="ml-1">(change in AI Settings)</span>
            </div>
          )}
        </DialogHeader>

        {proposal ? (
          <>
            <div className="px-5 py-4 bg-white"><DocAiPreview proposal={proposal} /></div>
            <div className="border-t border-slate-100 p-3 bg-white flex items-center justify-between gap-2">
              <Button variant="outline" size="sm" onClick={backToChat} className="gap-1.5" data-testid="ai-preview-back">
                <ArrowLeft className="h-3.5 w-3.5" />Back to chat to tweak
              </Button>
              <Button size="sm" onClick={apply} className="gap-1.5 text-white" style={{ backgroundColor: CRIMSON }} data-testid="ai-preview-apply">
                <Check className="h-3.5 w-3.5" />{isEdit ? "Apply to editor" : "Open in editor"}
              </Button>
            </div>
          </>
        ) : (
          <>
        {/* Transcript */}
        <div ref={scrollRef} className="px-5 py-4 space-y-3 overflow-auto bg-slate-50/50" style={{ height: "48vh" }} data-testid="ai-transcript">
          {starting ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8 px-6">
              <Wand2 className="h-8 w-8 mx-auto mb-3 text-slate-300" />
              {isEdit ? (
                <>
                  <p className="text-sm text-slate-600 font-medium">Ask about this form, or ask for changes</p>
                  <p className="text-xs text-slate-400 mt-1.5 max-w-sm mx-auto">e.g. "What fields are only shown when the answer is No?" or "Add a reason field after the storage question." When you're ready, hit <strong>Preview changes</strong> to see before and after.</p>
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-600 font-medium">Describe the form you want</p>
                  <p className="text-xs text-slate-400 mt-1.5 max-w-sm mx-auto">e.g. "A medication incident report for support workers." Attach example forms (PDF, image, Word/Excel) and I'll match them. We can go back and forth, then hit <strong>Preview form</strong>.</p>
                </>
              )}
            </div>
          ) : messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${m.role}`}>
              <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${m.role === "user" ? "whitespace-pre-wrap bg-slate-800 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"}`}>
                {m.role === "user" ? m.text : <ChatMarkdown>{m.text}</ChatMarkdown>}
                {m.role === "assistant" && m.plan && (
                  <div className="mt-2.5 inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-medium text-emerald-700" data-testid="ai-plan-badge">
                    <Check className="h-3 w-3" />Proposed plan — ready to preview
                  </div>
                )}
                {(m.files || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {m.files.map((f, j) => (
                      <span key={j} className={`inline-flex items-center gap-1 text-[10px] rounded px-1.5 py-0.5 ${m.role === "user" ? "bg-white/15" : "bg-slate-100 text-slate-500"}`}>
                        {f.kind === "image" ? <ImageIcon className="h-2.5 w-2.5" /> : <FileText className="h-2.5 w-2.5" />}{f.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {(sending || building) && sid && <AiWorking key={building ? "build" : "chat"} sid={sid} phase={building ? "build" : "chat"} />}
        </div>

        {/* Composer */}
        <div className="border-t border-slate-100 p-3 bg-white">
          {files.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="ai-pending-files">
              {files.map((f, i) => (
                <Badge key={i} variant="outline" className="text-[10px] gap-1 pr-1">
                  {f.name}
                  <button onClick={() => setFiles((cur) => cur.filter((_, j) => j !== i))} className="hover:text-rose-600"><X className="h-3 w-3" /></button>
                </Badge>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <input ref={fileRef} type="file" multiple hidden onChange={onPickFiles}
              accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt,.md,.html" data-testid="ai-file-input" />
            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => fileRef.current?.click()} disabled={sending || building} title="Attach example" data-testid="ai-attach-btn">
              <Paperclip className="h-4 w-4" />
            </Button>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={1}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={isEdit ? "Ask a question or describe a change…" : "Describe the form, or reply to the assistant…"} disabled={sending || building || starting}
              className="min-h-[38px] max-h-32 resize-none text-sm" data-testid="ai-input" />
            <Button size="icon" className="h-9 w-9 shrink-0" style={{ backgroundColor: CRIMSON }} onClick={send} disabled={sending || building || starting || (!text.trim() && files.length === 0)} data-testid="ai-send-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <div className="flex items-center justify-between mt-2.5">
            <span className={`text-[10px] ${planReady ? "text-emerald-600" : "text-slate-400"}`} data-testid="ai-plan-hint">
              {planReady
                ? "Plan ready — preview it, or keep chatting to adjust it."
                : isEdit ? "Ask a question, or describe a change. Preview unlocks when I propose a plan." : "Describe the form — Preview unlocks when I propose a plan."}
            </span>
            <Button size="sm" onClick={build} disabled={!planReady || building || sending}
              title={planReady ? undefined : "Available once the AI has proposed a plan"}
              className="gap-1.5" style={{ backgroundColor: planReady ? CRIMSON : undefined }} data-testid="ai-build-btn">
              {building ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {building ? (isEdit ? "Working out changes…" : "Building…") : isEdit ? "Preview changes" : "Accept & preview"}
            </Button>
          </div>
        </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
